import React, { useEffect, useState } from "react";
import PortalPage from "../PortalPage"
import { ReactComponent as ReportingIcon } from 'assets/icons/reports-filled.svg';
import { ReactComponent as BuildCircle } from 'assets/icons/build-circle.svg';
import { ReactComponent as AddChart } from 'assets/icons/add-chart.svg';

import { LabelledCheckbox } from "../../ui/LabelledCheckbox";
import { IConfirmProps } from "../../ui/ConfirmationBox";

import { Paper, Tabs, Tab } from "@mui/material";
import Chip from "@mui/material/Chip";

import { TabPanel } from 'components/ui/TabPanel';
import { List, IListItemProps } from 'components/ui/Lists';

import * as api from 'shared/services/reportingApi';

import {
    SupervisorAccountOutlined, PersonOutlined, NearMeOutlined, MailOutline,
    LocationOnOutlined, AssignmentTurnedInOutlined, CancelOutlined, DateRangeOutlined, CheckOutlined
} from '@mui/icons-material';
import { blobDownload } from "./blobDownload";
import { DrivingExemptionStatusEnum, FatigueAssessmentReportQuery, GNHExemptionReportQuery } from "../../../shared/models/api/models";
import DateRangePicker from "./DateRangePicker";

import 'react-datepicker/dist/react-datepicker.css';
import 'components/ui/ConfirmationBox.css';
import 'components/portal/administration/administration.css';
import './Reporting.css';

export const UserListItem = (props: IListItemProps<api.User>) =>
    <div className="list-item user-list-item" onClick={() => props.toggle(props.item)}>
        <LabelledCheckbox checkedValue={props.selected === true}><span className="name">{props.item.fullName}</span></LabelledCheckbox>
        <div>
            {props.item.city &&
                <span className="location"><NearMeOutlined /> {props.item.city}</span>
            }

            {props.item.supervisor?.length > 0 &&
                <span className="supervisor"><SupervisorAccountOutlined />{props.item.supervisor}</span>
            }

            {!props.item.city && props.item.email?.length > 0 &&
                <span className="email"><MailOutline />{props.item.email}</span>
            }

        </div>
    </div>;

interface IChipsProps<T> {
    values: T[],
    removeItem: (item: T) => void,
    prefix?: any
}

export const UserChips = (props: IChipsProps<api.User>) =>
    <>
        {props.values &&
            props.values.map(usr =>
                <Chip
                    key={usr.id}
                    onDelete={() => { props.removeItem(usr); }}
                    label={<>
                        <b>{props.prefix ? props.prefix + ": " : ""}</b>{usr.fullName}
                    </>} />
            )
        }
    </>;

const GNHExemptionReport = () => {
    const [exportState, setExportState] = useState("ready" as "ready" | "busy");
    const [stateVersion, setStateVersion] = useState(0);
    const [confirmProps, setConfirmProps] = useState(null as IConfirmProps | null);
    const [userFilterTab, setUserFilterTab] = useState(0);

    const [allUsers, setAllUsers] = useState([] as api.User[]);
    const [selectedUsers, setSelectedUsers] = useState([] as api.User[]);
    const [selectAllUsers, setSelectAllUsers] = useState(true);
    const [usersByNameFilter, setUsersByNameFilter] = useState("");
    const [usersByNameExpanded, setUsersByNameExpanded] = useState(false);
    const [usersByNameSummary, setUsersByNameSummary] = useState("");

    const [selectedSupervisors, setSelectedSupervisors] = useState([] as api.User[]);
    const [supervisorsFilter, setSupervisorsFilter] = useState("");
    const [supervisorsExpanded, setSupervisorsExpanded] = useState(false);
    const [supervisorsSummary, setSupervisorsSummary] = useState("");
    const [supervisors, setSupervisors] = useState([] as api.User[]);

    const [statusRequested, setStatusRequested] = useState(true);
    const [statusEndorsed, setStatusEndorsed] = useState(true);
    const [statusDeclined, setStatusDeclined] = useState(true);
    const [statusDeptManagerApproved, setStatusDeptManagerApproved] = useState(true);
    const [statusDeptManagerDeclined, setStatusDeptManagerDeclined] = useState(true);
    const [statusGMApproved, setStatusGMApproved] = useState(true);
    const [statusGMDeclined, setStatusGMDeclined] = useState(true);
    const [statusApproved, setStatusApproved] = useState(true);
    const [statusExpired, setStatusExpired] = useState(true);

    const [includeAudit, setincludeAudit] = useState(false);

    const refreshUsersFromResult = async (result: api.User[]) => {
        if (result) setAllUsers(result);
    };

    const refreshSupervisorsFromResult = async (result: api.User[]) => {
        if (result) setSupervisors(result);
    };

    interface IRemoteUpdate {
        getData: () => Promise<any[] | null>;
        processData: (result: any[] | null) => void;
    };

    const remoteUpdates = [
        { getData: api.getUsers, processData: refreshUsersFromResult },
        { getData: api.getSupervisors, processData: refreshSupervisorsFromResult }
    ] as IRemoteUpdate[];

    const processRemoteResults = (results: PromiseSettledResult<any[] | null>[]) => {
        results.forEach((result, i) => {
            if (result.status === "fulfilled" && result.value != null) {
                remoteUpdates[i].processData(result.value);
            }
            else {
                const errorMessage = `error retrieving ${remoteUpdates[i].getData.name}`;
                console.error(errorMessage);
            }
        });

    }

    useEffect(() => {
        let mounted = true;

        const mount = async () => {
            let results = await Promise.allSettled(remoteUpdates.map(r => r.getData()));
            if (mounted && results) {
                processRemoteResults(results);
            }
        };

        mount();

        return () => { mounted = false };
    }, []);

    const today = new Date()
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const [requestDateFrom, setRequestDateFrom] = useState(yesterday as Date | null);
    const [requestDateTo, setRequestDateTo] = useState(today as Date | null);

    const setRequestDateRange = (dateFrom: Date | null, dateTo: Date | null): void => {
        setRequestDateFrom(dateFrom);
        setRequestDateTo(dateTo);
    };

    const forceStateChange = () => {
        setStateVersion(stateVersion + 1);
    };

    const getExportFilter = () => {
        let userIds = selectedUsers.map(user => user.id);
        let supervisorIds = selectedSupervisors.map(user => user.id);

        let statusIds = [] as DrivingExemptionStatusEnum[];
        if (statusRequested) statusIds.push(DrivingExemptionStatusEnum.Requested);
        if (statusEndorsed) statusIds.push(DrivingExemptionStatusEnum.InitialReviewApproved);
        if (statusDeclined) statusIds.push(DrivingExemptionStatusEnum.InitialReviewRejected);
        if (statusDeptManagerApproved) statusIds.push(DrivingExemptionStatusEnum.DeptManagerApproved);
        if (statusDeptManagerDeclined) statusIds.push(DrivingExemptionStatusEnum.DeptManagerRejected);
        if (statusGMApproved) statusIds.push(DrivingExemptionStatusEnum.GMApproved);
        if (statusGMDeclined) statusIds.push(DrivingExemptionStatusEnum.GMRejected);
        if (statusApproved) statusIds.push(DrivingExemptionStatusEnum.Approved);
        if (statusExpired) statusIds.push(DrivingExemptionStatusEnum.Expired);
        if (statusExpired) statusIds.push(DrivingExemptionStatusEnum.InitialReviewCancelled);
        if (statusExpired) statusIds.push(DrivingExemptionStatusEnum.FinalReviewCancelled);

        return {
            userIds: userIds,
            supervisorIds: supervisorIds,
            statusIds: statusIds,
            requestDateFrom: requestDateFrom,
            requestDateTo: requestDateTo,
            includeAudit: includeAudit
        } as GNHExemptionReportQuery;
    };

    const downloadExportResults = async () => {
        if (exportState === "ready") {
            const filter = getExportFilter();

            if (filter) {
                setExportState("busy");

                api.downloadGNHExemptionResults(filter)
                    .then(file => {
                        setExportState("ready");
                        blobDownload(file);
                    })
                    .catch(e => {
                        const message = (e instanceof RangeError) ? e.message : "Error fetching data. ";
                        confirm(message, ["Close"], "Close");
                        setExportState("ready");
                    });
            }
        }
    };

    // when the user changes a user filter, turn on exporting users, and turn off "Select All Users"
    const userFilterAdded = () => {
        if (selectAllUsers) {
            setSelectAllUsers(false);
        }
    }

    const toggleUser = (usr: api.User) => {
        if (toggleItem(usr, selectedUsers, u => { return u.id === usr.id }, setSelectedUsers) === true) {
            userFilterAdded();
        }
    };

    const toggleSupervisor = (usr: api.User) => {
        if (toggleItem(usr, selectedSupervisors, u => { return u.id === usr.id }, setSelectedSupervisors) === true) {
            userFilterAdded();
        }
    };

    const toggleItem = <T,>(
        item: T,
        arr: T[],
        comparerPredicate: (value: T) => boolean,
        setter: (value: T[]) => void
    ): boolean => {
        const idx = arr.findIndex(comparerPredicate);

        var result = false;

        if (idx === -1) {
            result = true;
            arr.push(item);
        } else {
            arr.splice(idx, 1);
        }

        setter(arr);
        forceStateChange();

        return result;
    };

    const searchUsersByName = async (name: string) => {
        const result = await api.getUsersByName(name);

        if (result) {
            setAllUsers(result);
            setUsersByNameSummary(result.length + " search results for the search \"" + name + "\"");
        }

        return result;
    };

    const searchSupervisorsByName = async (name: string) => {
        const result = await api.getSupervisorsByName(name);

        if (result) {
            setSupervisors(result);
            setSupervisorsSummary(result.length + " search results for the search \"" + name + "\"");
        }

        return result;
    };

    const selectItems = <T,>(
        srcItems: T[],
        destinationItems: T[],
        comparer: (a: T, b: T) => boolean,
        setter: (items: T[]) => void
    ) => {
        for (var i = 0; i < srcItems.length; i++) {
            // if its not already selected, add it to the list
            if (destinationItems.findIndex(d => comparer(d, srcItems[i])) === -1) {
                destinationItems.push(srcItems[i]);
            }
        }

        setter(destinationItems);
        forceStateChange();
    };

    const confirm = async (message: string, opts?: string[], defaultOption?: string, cancelOption?: string): Promise<string> => {
        return new Promise<string>((resolve) => {
            setConfirmProps({
                message: message,
                options: opts ?? ["OK", "Cancel"],
                defaultOption: defaultOption,
                setResult: (value) => {
                    setConfirmProps({} as IConfirmProps);
                    resolve(value);
                }
            });
        });
    };

    const setSelectAllUsersAndClearSelections = (value: boolean) => {
        if (value) {
            confirm("Are you sure you want to include all users? Including all users will reset all user filters.", ["All Users", "Cancel"], "All Users").then(result => {
                if (result === "All Users") {
                    setSelectedUsers([]);
                    setSelectedSupervisors([]);

                    setSelectAllUsers(true);
                } else {
                    setSelectAllUsers(false);
                }
            });
        } else {
            setSelectAllUsers(value);
        }
    };

   
        return (
            <PortalPage module=""
                page="reporting"
                title={"GNH Exemption reporting"}
                route={"/reporting/gnh-exemption"}
                loadingState={exportState}
                icon={ReportingIcon}
                confirmationProps={confirmProps}>
            <div className="main-body-container reporting-module">

                <div className="section-1">
                    <div className="header">
                        <span className="section-1 icon"><BuildCircle /></span>
                        <span className="section-1 text">Build a report</span>
                    </div>
                </div>

                <div className="section-2">
                    <div className="header">
                        <span className="icon"><AddChart /></span>
                        <span className="text">Add data</span>
                    </div>
                </div>

                <div className="section-3">
                    <div className="header">
                        <span className="icon"><PersonOutlined /></span>
                        <span className="text">User</span>
                    </div>
                    <div className="divider" />
                </div>

                <div className="section-4">
                    <div className="body">
                        <LabelledCheckbox
                            checkedValue={selectAllUsers}
                            toggle={() => { setSelectAllUsersAndClearSelections(!selectAllUsers); }}>Select all users</LabelledCheckbox>
                    </div>
                    <div className="body">or</div>
                    <div className="body">
                        <Paper>
                            <Tabs value={userFilterTab} onChange={(e, v) => { setUserFilterTab(v); }}>
                                <Tab label="Find a user" />
                                <Tab label="Users by supervisor" />
                            </Tabs>

                            <TabPanel value={userFilterTab} index={0}>
                                <List<api.User>
                                    summary={usersByNameSummary}
                                    dataSource={allUsers}
                                    listItemType={UserListItem}
                                    filterText={usersByNameFilter}
                                    setFilterText={setUsersByNameFilter}
                                    expanded={usersByNameExpanded}
                                    setExpanded={setUsersByNameExpanded}
                                    buildKey={item => item.id}
                                    filterAsync={searchUsersByName}
                                    toggleSelected={toggleUser}
                                    selectItems={
                                        (filteredItems) => selectItems(
                                            filteredItems,
                                            selectedUsers,
                                            (a, b) => a.id === b.id,
                                            setSelectedUsers
                                        )
                                    }
                                    noResultsMessage="No users found"
                                    filterTextPlaceholder="Enter first name, last name or email address"
                                    selected={selectedUsers} />
                            </TabPanel>

                            <TabPanel value={userFilterTab} index={1}>
                                <List<api.User>
                                    summary={supervisorsSummary}
                                    dataSource={supervisors}
                                    listItemType={UserListItem}
                                    filterText={supervisorsFilter}
                                    setFilterText={setSupervisorsFilter}
                                    expanded={supervisorsExpanded}
                                    setExpanded={setSupervisorsExpanded}
                                    buildKey={item => item.id}
                                    filterAsync={searchSupervisorsByName}
                                    toggleSelected={toggleSupervisor}
                                    selectItems={
                                        (filteredItems) => selectItems(
                                            filteredItems,
                                            selectedSupervisors,
                                            (a, b) => a.id === b.id,
                                            setSelectedSupervisors
                                        )
                                    }
                                    noResultsMessage="No supervisors found"
                                    filterTextPlaceholder="Enter first name, last name or email address"
                                    selected={selectedSupervisors} />
                            </TabPanel>

                        </Paper>

                        <span>
                            <UserChips prefix="User" values={selectedUsers} removeItem={toggleUser} />
                            <UserChips prefix="Supervisor" values={selectedSupervisors} removeItem={toggleSupervisor} />
                            &nbsp;
                            {selectAllUsers &&
                                <Chip onDelete={() => { setSelectAllUsersAndClearSelections(false); }} label={<><b>User: </b>All</>}></Chip>
                            }
                            &nbsp;
                        </span>
                    </div>
                </div>

                <div className="section-3">
                    <div className="header">
                        <span className="icon"><AssignmentTurnedInOutlined /></span>
                        <span className="text">Status</span>
                    </div>
                    <div className="divider" />
                </div>

                <div className="section-4">
                    <div className="body">
                        <LabelledCheckbox checkedValue={statusRequested} toggle={() => setStatusRequested(!statusRequested)}>Requested</LabelledCheckbox>
                        <LabelledCheckbox checkedValue={statusEndorsed} toggle={() => setStatusEndorsed(!statusEndorsed)}>Endorsed</LabelledCheckbox>
                        <LabelledCheckbox checkedValue={statusDeclined} toggle={() => setStatusDeclined(!statusDeclined)}>Not Endorsed</LabelledCheckbox>
                        <LabelledCheckbox checkedValue={statusDeptManagerApproved} toggle={() => setStatusDeptManagerApproved(!statusDeptManagerApproved)}>Department Manager Approved</LabelledCheckbox>
                        <LabelledCheckbox checkedValue={statusDeptManagerDeclined} toggle={() => setStatusDeptManagerDeclined(!statusDeptManagerDeclined)}>Department Manager Declined</LabelledCheckbox>
                        <LabelledCheckbox checkedValue={statusGMApproved} toggle={() => setStatusGMApproved(!statusGMApproved)}>GM Approved</LabelledCheckbox>
                        <LabelledCheckbox checkedValue={statusGMDeclined} toggle={() => setStatusGMDeclined(!statusGMDeclined)}>GM Declined</LabelledCheckbox>
                        <LabelledCheckbox checkedValue={statusApproved} toggle={() => setStatusApproved(!statusApproved)}>Approved</LabelledCheckbox>
                        <LabelledCheckbox checkedValue={statusExpired} toggle={() => setStatusExpired(!statusExpired)}>Expired</LabelledCheckbox>
                    </div>
                </div>

                <div className="section-3">
                    <div className="header">
                        <span className="icon"><DateRangeOutlined /></span>
                        <span className="text">Request Start Date</span>
                    </div>
                    <div className="divider" />
                </div>

                <div className="section-4">
                    <div className="body">
                        <DateRangePicker
                            fromDate={requestDateFrom ?? yesterday}
                            toDate={requestDateTo ?? today}
                            setter={(f, t) => {
                                setRequestDateRange(f ? f : null, t ? t : null);
                            }}
                        />
                    </div>
                </div>
                <div className="section-3">
                    <div className="header">
                        <span className="icon"><CheckOutlined /></span>
                        <span className="text">Audit</span>
                    </div>
                    <div className="divider" />
                </div>
                <div className="section-4">
                    <div className="body">
                        <LabelledCheckbox checkedValue={includeAudit} toggle={() => setincludeAudit(!includeAudit)}>Include GNH exemption audit trail</LabelledCheckbox>

                    </div>
                </div>
                <div className="section-3">
                    <div className="body" style={{ textAlign: "center" }}>
                        <button className={"button-face export-state-" + exportState} onClick={downloadExportResults}>Export to CSV</button>
                    </div>
                </div>

                {((exportState === 'busy')) &&
                    <div className="export-busy-container">
                        <div className="export-busy-message">We're fetching your data...</div>
                    </div>
                }

                </div>
            </PortalPage>
        );
};

export default GNHExemptionReport;